import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
} from 'components/ui/dialog';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { Cable, X, Palette, BrainCircuit, CreditCard, User, Archive } from 'lucide-react';
import { ConnectorsTab } from './tabs/connectors-tab';
import { ThemeTab } from './tabs/theme-tab';
import { PLANS } from 'constant/plan';
//import { ComingSoonTab } from './tabs/coming-soon-tab';
import { DialogTitle } from '@radix-ui/react-dialog';
import { SubscriptionPlan } from '/features/subscription';
import { ComingSoonTab } from './tabs/coming-soon-tab';
import { PersonalizationTab } from './tabs/personalization-tab';
import { SubscriptionTab } from './tabs/subscription-tab';
import { AccountTab } from './tabs/account-tab';
import { ArchiveTab } from './tabs/archive-tab';

/**
 * Settings Dialog Component
 * 
 * A comprehensive settings dialog that provides access to different application settings
 * organized in tabs including General, Connectors, Notifications, Personalization, and Account.
 * 
 * Features:
 * - Tabbed navigation for different settings categories
 * - Responsive design with theme support
 * - Integration with existing UI components
 * - Proper accessibility and keyboard navigation
 * 
 * @param isOpen - Controls whether the dialog is displayed
 * @param setIsOpen - Callback function to control dialog visibility
 * @returns A settings dialog component
 */

interface SettingsDialogProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    currentPlan: SubscriptionPlan | null;
    initialTab?: string;
    selectedConnectorId?: string | null;
    hideNavigation?: boolean;
}

export const SettingsDialog = ({ isOpen, setIsOpen, currentPlan, initialTab, selectedConnectorId, hideNavigation }: SettingsDialogProps) => {
    const { t } = useTranslation();

    // Hide connectors tab if:
    // - No plan exists, OR
    // - User has NOPLAN, FREE, or BASIC plan
    // Only show connectors for paid plans (BUSINESS, ENTERPRISE, etc.)
    const shouldHideConnectors = !(currentPlan && (currentPlan.name !== PLANS.NOPLAN && currentPlan.name !== PLANS.FREE && currentPlan.name !== PLANS.BASIC));
    const shouldShowArchive = currentPlan && currentPlan.name !== PLANS.NOPLAN;

    const tabs = useMemo(() => {
        const allTabs = [
            {
                id: 'account',
                label: t('ACCOUNT') || 'Account',
                icon: User,
                component: AccountTab,
                hide: false
            },
            {
                id: 'personalization',
                label: t('PERSONALIZATION') || 'Personalization',
                icon: BrainCircuit,
                component: PersonalizationTab,
                hide: false
            },
            {
                id: 'subscription',
                label: t('SUBSCRIPTION') || 'Subscription',
                icon: CreditCard,
                component: SubscriptionTab,
                hide: false
            },
            {
                id: 'archive',
                label: t('ARCHIVED_CHATS') || 'Archived Chats',
                icon: Archive,
                component: ArchiveTab,
                hide: !shouldShowArchive //Show only for users with a plan
            },
            {
                id: 'connectors',
                label: t('CONNECTORS') || 'Connectors',
                icon: Cable,
                component: ConnectorsTab,
                hide: shouldHideConnectors //shouldHideConnectors true means it will be hidden
            },
            {
                id: 'theme',
                label: t('THEME') || 'Theme',
                icon: Palette,
                component: ThemeTab,
                hide: true
            },
            {
                id: 'about',
                label: t('ABOUT') || 'About',
                icon: Cable,
                component: ComingSoonTab,
                hide: true
            }
        ];
        return allTabs.filter(tab => !tab.hide);
    }, [t, shouldHideConnectors, shouldShowArchive]);

    const getInitialTab = () => {
        if (initialTab) {
            const tabExists = tabs.find(tab => tab.id === initialTab);
            if (tabExists) {
                return initialTab;
            }
        }
        return tabs[0]?.id || 'theme';
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());

    // Reset to first tab when dialog opens
    React.useEffect(() => {
        if (isOpen) {
            setActiveTab(getInitialTab());
        }
    }, [isOpen, initialTab, tabs]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="md:max-w-4xl h-[85vh] md:h-[85vh] sm:max-w-[95vw] p-0" hideClose>
                <DialogTitle className="sr-only"></DialogTitle>
                <div className="h-full flex flex-col overflow-hidden">
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Mobile header - conditionally hide tabs when hideNavigation is true */}
                        {!hideNavigation && (
                            <div className="md:hidden bg-light-gray border-b border-border p-3 flex-shrink-0">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-base font-semibold text-foreground">Settings</h2>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="w-7 h-7 flex items-center justify-center group transition-opacity hover:opacity-80 border-2 border-gray-600 rounded-full"
                                        aria-label="Close dialog"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
                                    </button>
                                </div>

                                {/* Mobile tab navigation */}
                                <div className="flex flex-wrap gap-1.5 overflow-x-auto">
                                    {tabs.map((tab) => {
                                        const IconComponent = tab.icon;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${activeTab === tab.id
                                                    ? 'bg-medium-gray text-foreground'
                                                    : 'text-muted-foreground hover:bg-accent/70'
                                                    }`}
                                            >
                                                <IconComponent className="h-3.5 w-3.5" />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Show close button on mobile when navigation is hidden */}
                        {hideNavigation && (
                            <div className="md:hidden bg-light-gray border-b border-border p-3 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-base font-semibold text-foreground">Settings</h2>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="w-7 h-7 flex items-center justify-center group transition-opacity hover:opacity-80 border-2 border-gray-600 rounded-full"
                                        aria-label="Close dialog"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
                                    </button>
                                </div>
                            </div>
                        )}

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full overflow-hidden">
                            {/* Desktop sidebar - conditionally hide when hideNavigation is true */}
                            {!hideNavigation && (
                                <div className="hidden md:flex w-48 border-r border-border bg-light-gray flex-col flex-shrink-0">
                                    {/* Custom Close Button */}
                                    <div className="py-3">
                                        <button
                                            onClick={() => setIsOpen(false)}
                                            className="w-5 h-5 relative overflow-hidden flex items-center justify-center group transition-opacity hover:opacity-80 ml-3 border-2 border-gray-600 rounded-full"
                                            aria-label="Close dialog"
                                        >
                                            <X className="w-[12px] h-[12px] text-gray-600" strokeWidth={4} />
                                        </button>
                                    </div>

                                    <TabsList className="flex flex-col items-start justify-start bg-transparent p-3 gap-1 h-auto">
                                        {tabs.map((tab) => {
                                            const IconComponent = tab.icon;
                                            return (
                                                <TabsTrigger
                                                    key={tab.id}
                                                    value={tab.id}
                                                    className="w-full justify-start gap-2 px-3 py-2 text-left text-[13px] font-medium text-foreground data-[state=active]:bg-medium-gray data-[state=active]:text-foreground hover:bg-accent/70 rounded-lg data-[state=inactive]:bg-transparent border-none outline-none"
                                                >
                                                    <div className="h-4 w-4">
                                                        <IconComponent className="h-4 w-4" />
                                                    </div>
                                                    {tab.label}
                                                </TabsTrigger>
                                            );
                                        })}
                                    </TabsList>
                                </div>
                            )}

                            <div className="flex-1 overflow-hidden">
                                {tabs.map((tab) => {
                                    const ComponentToRender = tab.component;
                                    return (
                                        <TabsContent key={tab.id} value={tab.id} className="p-0 m-0 h-full">
                                            <ComponentToRender
                                                tabId={tab.id}
                                                {...(tab.id === 'connectors' && {
                                                    selectedConnectorId,
                                                    onCloseModal: () => setIsOpen(false)
                                                })}
                                            />
                                        </TabsContent>
                                    );
                                })}
                            </div>
                        </Tabs>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
